export type RequestFailure =
  | { kind: 'connection' }
  | { kind: 'server'; status: number; code: string | null }
  | { kind: 'unknown' };

const NO_RESPONSE =
  /failed to fetch|fetch failed|networkerror when attempting to fetch|^load failed$|err_internet_disconnected/i;

export function classifyRequestFailure(error: unknown): RequestFailure {
  const candidate = error as {
    data?: { httpStatus?: unknown; code?: unknown };
    meta?: { response?: { status?: unknown } };
    cause?: unknown;
  } | null;
  const status =
    typeof candidate?.data?.httpStatus === 'number'
      ? candidate.data.httpStatus
      : candidate?.meta?.response?.status;
  if (typeof status === 'number' && status > 0) {
    const code = candidate?.data?.code;
    return {
      kind: 'server',
      status,
      code: typeof code === 'string' ? code : null,
    };
  }
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return { kind: 'connection' };
  }
  const messages = [error, candidate?.cause].map((part) =>
    part instanceof Error ? part.message : typeof part === 'string' ? part : '',
  );
  return messages.some((message) => NO_RESPONSE.test(message.trim()))
    ? { kind: 'connection' }
    : { kind: 'unknown' };
}

/**
 * Converts a raw error (often from TRPC mutations) into a user-friendly
 * message suitable for toast notifications.
 *
 * Strips technical jargon (TRPC codes, HTTP status, SQL references) and
 * returns actionable guidance instead.
 */
export function friendlyErrorMessage(
  error: unknown,
  fallback = "Something didn't work. Try again.",
): string {
  const raw =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : '';

  if (!raw) return fallback;

  const failure = classifyRequestFailure(error);
  const signal =
    failure.kind === 'server' && failure.code ? `${raw} ${failure.code}` : raw;

  // Network / connectivity issues
  if (failure.kind === 'connection') {
    return "The server didn't answer. Check your connection and try again.";
  }

  // Timeout
  if (signal.includes('timeout') || signal.includes('TIMEOUT')) {
    return 'The request took too long. Try again.';
  }

  // Rate limiting
  if (
    signal.includes('RATE_LIMIT') ||
    signal.includes('TOO_MANY_REQUESTS') ||
    signal.includes('Too many requests') ||
    signal.includes('rate limit')
  ) {
    return 'Too many requests. Wait a moment and try again.';
  }

  // Auth
  if (
    signal.includes('UNAUTHORIZED') ||
    signal.includes('not authenticated') ||
    signal.includes('authentication')
  ) {
    return 'Your session may have expired. Refresh the page to sign in again.';
  }

  // Forbidden / access denied
  if (
    signal.includes('FORBIDDEN') ||
    signal.includes('forbidden') ||
    signal.includes('access denied') ||
    signal.includes('not allowed')
  ) {
    return "You don't have permission to do that.";
  }

  // Not found
  if (signal.includes('NOT_FOUND') || /\bnot found\b/i.test(signal)) {
    return "The item you're looking for could not be found. It may have been deleted.";
  }

  // Conflict (e.g. duplicate name, concurrent edit)
  if (signal.includes('CONFLICT') || signal.includes('already exists')) {
    return 'This conflicts with an existing item. Try a different name, or refresh.';
  }

  // Payload too large
  if (
    signal.includes('PAYLOAD_TOO_LARGE') ||
    signal.includes('too large') ||
    signal.includes('exceeds the maximum')
  ) {
    return 'The content is too large. Reduce the size and try again.';
  }

  // Filter out technical-looking messages
  if (
    /^[A-Z_]{2,}$/.test(raw) || // Pure error codes
    /\b(TRPC|trpc|SQL|postgres|drizzle|prisma)\b/i.test(raw) ||
    /\bstatus\s*\d{3}\b/i.test(raw) ||
    (raw.includes('at ') && raw.includes('(')) // stack trace
  ) {
    return fallback;
  }

  // If the message looks human-readable, use it
  if (raw.length < 200 && /^[A-Z]/.test(raw)) {
    return raw;
  }

  return fallback;
}
